import { useState, useEffect, useRef, useCallback } from 'react';
import { AttachmentItemType } from '../Attachments/AttachmentContainer/AttachmentsContainer';
import { UploadProgress } from '../Attachments/AttachmentItem/AttachmentsItem';

export interface UseFileUploadReturn {
  // State
  attachments: AttachmentItemType[];
  uploadProgress: UploadProgress;
  fileInputRef: React.RefObject<HTMLInputElement>;

  // Handlers
  handleOpenUploadFile: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteAttachment: (id: number) => void;
  addFile: (file: File) => void;

  // State setters (for external reset)
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentItemType[]>>;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
}

/**
 * Hook that encapsulates file upload and attachment management logic
 */
export function useFileUpload(): UseFileUploadReturn {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    percentage: 0,
    status: 'pending',
  });
  const [attachments, setAttachments] = useState<AttachmentItemType[]>([]);

  const handleOpenUploadFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
    // Reset the input value to allow re-selecting the same file
    event.target.value = '';
  }, []);

  const handleDeleteAttachment = useCallback((id: number) => {
    setFile(null);
    setAttachments(prev => prev.filter(attachment => attachment.id !== id));
  }, []);

  const simulateFileUpload = useCallback((fileToUpload: File) => {
    setUploadProgress({ percentage: 0, status: 'uploading' });

    const latency = 1000;
    let uploadedBytes = 0;
    const totalBytes = fileToUpload.size;
    const chunkSize = 1024 * 100;
    let intervalId: NodeJS.Timeout | null = null;

    const uploadChunk = () => {
      uploadedBytes += chunkSize;
      const newPercentage = Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
      setUploadProgress(prevProgress => ({ ...prevProgress, percentage: newPercentage }));

      if (uploadedBytes >= totalBytes) {
        if (intervalId) clearInterval(intervalId);
        setTimeout(() => {
          setUploadProgress({ percentage: 100, status: 'success' });
        }, 500);
      }
    };

    intervalId = setInterval(uploadChunk, latency / 10);
  }, []);

  // Add a file programmatically (e.g., from paste or drag-and-drop)
  const addFile = useCallback((newFile: File) => {
    setFile(newFile);
  }, []);

  // Update attachment progress when uploadProgress changes
  useEffect(() => {
    if (uploadProgress) {
      setAttachments(prev =>
        prev.map(attachment => {
          if (attachment.id === prev.length) {
            return { ...attachment, processing: uploadProgress };
          }
          return attachment;
        })
      );
    }
  }, [uploadProgress]);

  // Add new attachment when file is selected
  useEffect(() => {
    if (file) {
      const newAttachment: AttachmentItemType = {
        id: attachments.length + 1,
        type: file.type.split('/')[1],
        name: file.name,
        file: file,
      };
      simulateFileUpload(file);
      setAttachments(prev => [...prev, newAttachment]);
      // Reset file state to allow adding more files
      setFile(null);
    }
  }, [file, simulateFileUpload]);

  return {
    attachments,
    uploadProgress,
    fileInputRef,
    handleOpenUploadFile,
    handleFileChange,
    handleDeleteAttachment,
    addFile,
    setAttachments,
    setFile,
  };
}
